/**
 * Pályaformátum — „szoba-alapú" leírás.
 *
 * A pálya konvex/konkáv XZ-poligonokból (szobákból) áll, mindegyiknek saját
 * padló- és mennyezetmagassága van. A szobák közti átjárókat portálok írják le:
 * egy portál a két szoba közös élének egy szakasza. A geometria- és
 * kollízió-építő ebből származtat mindent — nincs külön szerkesztő,
 * mégis van vertikalitás (emelvény, erkély, ablak, lift).
 *
 * Gazda: C — X ne módosítsa, csak olvassa.
 */

import type { Vec2 } from '../core/math'

export type TexId = string
export type SfxId = string
export type KeyColor = 'blue' | 'yellow' | 'red'

export interface RoomDef {
  id: number
  /** XZ-poligon, óramutatóval ellentétes (CCW) körüljárással. */
  poly: Vec2[]
  floorY: number
  ceilY: number
  /** Alap fényszint 0..1 — ez adja a Doom-os szektoronkénti hangulatot. */
  light: number
  tex: { floor: TexId; ceil: TexId; wall: TexId }
  /** Ha igaz, a mennyezet helyett égbolt látszik. */
  sky?: boolean
  /** Sebzés másodpercenként, ha a játékos a padlón áll (savas/mérgező tér). */
  hazard?: number
  /** Titkos szoba — felfedezéskor jóváíródik. */
  secret?: boolean
  /** A szoba padlója mozog (lift/emelvény). */
  lift?: LiftDef
  name?: string
}

/**
 * Emelkedő/süllyedő padló. A liftet NEM portál írja le, hanem a szoba maga:
 * a szoba padlója mozog `lowY` és `highY` között. Így a geometria és a
 * kollízió is egyetlen helyről származik, és a lift alatti/fölötti tér
 * konzisztens marad.
 */
export interface LiftDef {
  lowY: number
  highY: number
  /** Egység/mp. */
  speed: number
  /** Hány másodpercig marad a távoli állásban; 0 = ott marad. */
  waitTime: number
  /** Alaphelyzet: lent vagy fent. */
  startAt: 'low' | 'high'
  /** Ha van, csak ezzel a taggel (kapcsolóval) indítható. */
  tag?: string
  /** Ráállásra automatikusan indul (klasszikus Doom-lift). */
  autoTrigger?: boolean
}

export interface DoorDef {
  /** A lap felfelé csúszik a nyílás magasságáig. */
  kind: 'door'
  /** Nyitási sebesség egység/mp-ben. */
  speed: number
  /** Hány másodpercig marad nyitva; 0 = örökre. */
  waitTime: number
  /** Ha meg van adva, ilyen színű kulcs kell a nyitáshoz. */
  key?: KeyColor
  tex: TexId
  /** Csak `tag`-gel (kapcsolóval) nyitható, kézzel nem. */
  remoteOnly?: boolean
  tag?: string
}

export interface PortalDef {
  id: number
  /** A két összekötött szoba azonosítója. */
  rooms: [number, number]
  /** A közös szakasz — pontosan mindkét szoba egy élére kell illeszkednie. */
  p0: Vec2
  p1: Vec2
  /** Az átjáró alsó pereme; alapértelmezés: a két padló maximuma. */
  bottom?: number
  /** Az átjáró felső pereme; alapértelmezés: a két mennyezet minimuma. */
  top?: number
  door?: DoorDef
}

export interface PropDef {
  id: number
  kind: 'box' | 'cylinder'
  pos: Vec2
  /** box: fél-méret X/Z-ben. cylinder: `x` a sugár, `z` figyelmen kívül. */
  size: Vec2
  yaw?: number
  bottom: number
  top: number
  tex: TexId
  /** Ütközik-e vele a játékos és az ellenség. */
  solid: boolean
  /** 0..1 önfény — világító panelekhez, lávához. */
  emissive?: number
  emissiveColor?: number
}

export interface ThingDef {
  kind: 'playerStart' | 'enemy' | 'pickup' | 'decor' | 'exit' | 'switch'
  /** Archetípus-azonosító, pl. 'imp', 'shotgun', 'medkit', 'torch'. */
  type: string
  pos: Vec2
  yaw?: number
  /** Kapcsolóhoz/triggerhez: melyik ajtó-taget aktiválja. */
  tag?: string
  /** Encounter-csoport: ezek együtt ébrednek. */
  group?: string
}

export interface LightDef {
  pos: Vec2
  y: number
  color: number
  intensity: number
  distance: number
  style?: 'steady' | 'flicker' | 'pulse'
}

export interface LevelDef {
  id: string
  name: string
  subtitle?: string
  /** Az ég és a távoli köd színe. */
  skyColor: number
  fogColor: number
  /** Exponenciális köd sűrűsége. 0 = nincs köd. */
  fogDensity: number
  /** Globális ambiens fény erőssége 0..1. */
  ambient: number
  rooms: RoomDef[]
  portals: PortalDef[]
  props: PropDef[]
  things: ThingDef[]
  lights: LightDef[]
}

// ---------------------------------------------------------------------------
// Szerzői segédfüggvények — a pályaadat kézzel írásához.
// ---------------------------------------------------------------------------

export function v(x: number, z: number): Vec2 {
  return { x, z }
}

/** Tengelyre illesztett téglalap CCW poligonként, a középpontja (cx, cz). */
export function rect(cx: number, cz: number, halfW: number, halfD: number): Vec2[] {
  return [
    v(cx - halfW, cz - halfD),
    v(cx + halfW, cz - halfD),
    v(cx + halfW, cz + halfD),
    v(cx - halfW, cz + halfD),
  ]
}

/** Sarokpontokkal megadott téglalap. */
export function box(x0: number, z0: number, x1: number, z1: number): Vec2[] {
  return [v(x0, z0), v(x1, z0), v(x1, z1), v(x0, z1)]
}

/** Szabályos sokszög — kerek termekhez, oszlopcsarnokokhoz. */
export function ngon(cx: number, cz: number, radius: number, sides: number, rotation = 0): Vec2[] {
  const out: Vec2[] = []
  for (let i = 0; i < sides; i++) {
    const a = rotation + (i / sides) * Math.PI * 2
    out.push(v(cx + Math.cos(a) * radius, cz + Math.sin(a) * radius))
  }
  return out
}
